import { useState, useEffect } from 'react';
import { getUsers, getAdminClassTypes } from '../../../services/adminService';

function toDatetimeLocalInputValue(utcDateString) {
  if (!utcDateString) return '';
  const date = new Date(utcDateString);
  const pad = n => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function CreateClassModal({ show, onClose, onSubmit, editClass, isDuplicate, token }) {
  const [newClass, setNewClass] = useState({
    title: '',
    description: '',
    type: 'offline',
    start: '',
    durationMinutes: 60,
    capacity: '',
    locationName: '',
    locationUrl: '',
    videoCallUrl: '',
    level: 'all',
    language: 'russian',
    studioCost: '',
    isIndividual: false,
    classType: '',
    allowJoinOnline: false,
  });
  const [visibleForUsers, setVisibleForUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [classTypes, setClassTypes] = useState([]);

  const isEditMode = !!editClass && !isDuplicate;

  const isIndividualSelected = classTypes.find((ct) => ct._id === newClass.classType)?.isIndividual === true;

  // Load class types for the dropdown, defaulting new classes to the first group (non-individual) type
  useEffect(() => {
    if (!token || !show) return;
    getAdminClassTypes(token)
      .then((data) => {
        const active = (Array.isArray(data) ? data : []).filter((ct) => ct.active !== false);
        setClassTypes(active);
        if (!isEditMode) {
          const defaultType = active.find((ct) => !ct.isIndividual) || active[0];
          if (defaultType) {
            setNewClass((prev) => {
              if (prev.classType) return prev;
              const defaultIsOnline = defaultType.type === 'online';
              return {
                ...prev,
                classType: defaultType._id,
                type: defaultIsOnline ? 'online' : prev.type,
                title: defaultType.displayName || prev.title,
                description: defaultType.description || prev.description,
                locationName: defaultType.locationName || prev.locationName,
                locationUrl: defaultType.locationUrl || prev.locationUrl,
                videoCallUrl: defaultType.videoCallUrl || prev.videoCallUrl,
                durationMinutes: defaultType.durationMinutes != null ? defaultType.durationMinutes : prev.durationMinutes,
                capacity: defaultType.capacity != null ? defaultType.capacity : prev.capacity,
                studioCost: defaultType.studioCost != null ? defaultType.studioCost : prev.studioCost,
                allowJoinOnline: defaultIsOnline ? false : defaultType.allowJoinOnline === true,
              };
            });
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching class types:', error);
        setClassTypes([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, show]);

  // Load users for dropdown when Individual is selected
  useEffect(() => {
    if (!token || !isIndividualSelected || !show) {
      setAllUsers([]);
      return;
    }
    setUsersLoading(true);
    getUsers(token, 1, '')
      .then((data) => setAllUsers(data.users || []))
      .catch(() => setAllUsers([]))
      .finally(() => setUsersLoading(false));
  }, [token, isIndividualSelected, show]);

  useEffect(() => {
    if (editClass) {
      setNewClass({
        title: editClass.title || '',
        description: editClass.description || '',
        type: editClass.type || 'offline',
        start: toDatetimeLocalInputValue(editClass.start),
        durationMinutes: editClass.durationMinutes || 60,
        capacity: editClass.capacity ?? '',
        locationName: editClass.locationName || '',
        locationUrl: editClass.locationUrl || '',
        videoCallUrl: editClass.videoCallUrl || '',
        level: editClass.level || 'all',
        language: editClass.language || 'russian',
        studioCost: editClass.studioCost != null ? editClass.studioCost : '',
        classType: editClass.classType?._id || editClass.classType || '',
        allowJoinOnline: editClass.allowJoinOnline === true,
      });
      setVisibleForUsers([]);
    } else {
      setNewClass({
        title: '',
        description: '',
        type: 'offline',
        start: '',
        durationMinutes: 60,
        capacity: '',
        locationName: '',
        locationUrl: '',
        videoCallUrl: '',
        level: 'all',
        language: 'russian',
        studioCost: '',
        classType: '',
        allowJoinOnline: false,
      });
      setVisibleForUsers([]);
    }
  }, [editClass, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let startUtc = newClass.start;
    if (startUtc) {
      const localDate = new Date(startUtc);
      startUtc = localDate.toISOString();
    }

    const parsedDuration = newClass.durationMinutes !== '' && !Number.isNaN(Number(newClass.durationMinutes))
      ? parseInt(newClass.durationMinutes, 10)
      : 60;
    const parsedStudioCost = newClass.studioCost !== '' && !Number.isNaN(Number(newClass.studioCost))
      ? Number(newClass.studioCost)
      : null;
    const studioCostValue = parsedStudioCost !== null && parsedStudioCost < 0 ? null : parsedStudioCost;

    const payload = {
      ...newClass,
      start: startUtc,
      _id: isDuplicate ? undefined : editClass?._id,
      capacity: newClass.capacity !== '' && !Number.isNaN(parseInt(newClass.capacity, 10)) ? parseInt(newClass.capacity, 10) : null,
      durationMinutes: parsedDuration >= 0 ? parsedDuration : 60,
      studioCost: studioCostValue,
      classType: newClass.classType,
    };
    if (visibleForUsers.length > 0 || !isEditMode || !editClass?.visibleForUsers?.length) {
      payload.visibleForUsers = visibleForUsers.map(u => u.user_id);
    }
    onSubmit(payload);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">
            {isDuplicate ? 'Duplicate Class' : isEditMode ? 'Edit Class' : 'Create New Class'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1">Title</label>
            <input
              type="text"
              value={newClass.title}
              onChange={(e) => setNewClass({ ...newClass, title: e.target.value })}
              className="w-full border border-gray-300 rounded p-2 text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Description</label>
            <input
              type="text"
              value={newClass.description}
              onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
              className="w-full border border-gray-300 rounded p-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Type</label>
              <select
                value={newClass.type}
                onChange={(e) => setNewClass({ ...newClass, type: e.target.value })}
                className="w-full border border-gray-300 rounded p-2 text-sm"
              >
                <option value="offline">Offline</option>
                <option value="online">Online</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Level</label>
              <select
                value={newClass.level}
                onChange={(e) => setNewClass({ ...newClass, level: e.target.value })}
                className="w-full border border-gray-300 rounded p-2 text-sm"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Start Date & Time *</label>
              <input
                type="datetime-local"
                value={toDatetimeLocalInputValue(newClass.start)}
                onChange={e => setNewClass({ ...newClass, start: e.target.value })}
                className="w-full border border-gray-300 rounded p-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Duration (min)</label>
              <input
                type="number"
                value={newClass.durationMinutes}
                onChange={(e) => setNewClass({ ...newClass, durationMinutes: e.target.value })}
                className="w-full border border-gray-300 rounded p-2 text-sm"
                placeholder="60"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Capacity</label>
              <input
                type="number"
                value={newClass.capacity}
                onChange={(e) => setNewClass({ ...newClass, capacity: e.target.value })}
                className="w-full border border-gray-300 rounded p-2 text-sm"
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Studio cost (₪)</label>
              <input
                type="number"
                min="0"
                value={newClass.studioCost}
                onChange={(e) => setNewClass({ ...newClass, studioCost: e.target.value })}
                onWheel={(e) => e.currentTarget.blur()}
                className="w-full border border-gray-300 rounded p-2 text-sm"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Language</label>
              <select
                value={newClass.language}
                onChange={(e) => setNewClass({ ...newClass, language: e.target.value })}
                className="w-full border border-gray-300 rounded p-2 text-sm"
              >
                <option value="russian">Russian</option>
                <option value="english">English</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Class type</label>
              <select
                value={newClass.classType}
                onChange={(e) => {
                  const classType = e.target.value;
                  const ct = classTypes.find((c) => c._id === classType);
                  const nowIndividual = ct?.isIndividual === true;
                  const nowOnline = ct?.type === 'online';
                  setNewClass({
                    ...newClass,
                    classType,
                    type: nowOnline ? 'online' : newClass.type,
                    title: ct?.displayName || newClass.title,
                    description: ct?.description || newClass.description,
                    capacity: nowIndividual
                      ? 1
                      : ct?.capacity != null
                        ? ct.capacity
                        : (isIndividualSelected ? '' : newClass.capacity),
                    locationName: ct?.locationName || newClass.locationName,
                    locationUrl: ct?.locationUrl || newClass.locationUrl,
                    videoCallUrl: ct?.videoCallUrl || newClass.videoCallUrl,
                    durationMinutes: ct?.durationMinutes != null ? ct.durationMinutes : newClass.durationMinutes,
                    studioCost: ct?.studioCost != null ? ct.studioCost : newClass.studioCost,
                    allowJoinOnline: nowOnline ? false : ct?.allowJoinOnline === true,
                  });
                }}
                className="w-full border border-gray-300 rounded p-2 text-sm"
              >
                {classTypes.map((ct) => (
                  <option key={ct._id} value={ct._id}>{ct.displayName}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-0.5">Determines which pass types can book this class.</p>
            </div>
          </div>

          {isIndividualSelected && (
            <div>
              <label className="block text-sm font-semibold mb-1">Visible to</label>
              {isEditMode && editClass?.visibleForUsers?.length > 0 && visibleForUsers.length === 0 && (
                <p className="text-xs text-gray-500 mb-1">This class is visible to {editClass.visibleForUsers.length} user(s). Select users below to replace the list.</p>
              )}
              {usersLoading ? (
                <p className="text-xs text-gray-500 py-2">Loading users...</p>
              ) : (
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    const u = allUsers.find((x) => x.user_id === value);
                    if (u && !visibleForUsers.some((x) => x.user_id === value)) {
                      setVisibleForUsers(prev => [...prev, { user_id: u.user_id, name: u.name, email: u.email }]);
                    }
                    e.target.value = '';
                  }}
                  className="w-full border border-gray-300 rounded p-2 text-sm"
                >
                  <option value="">Select a user...</option>
                  {allUsers.map((u) => (
                    <option
                      key={u.user_id}
                      value={u.user_id}
                      disabled={visibleForUsers.some((x) => x.user_id === u.user_id)}
                    >
                      {u.name || u.email || u.user_id}
                      {u.email ? ` (${u.email})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {visibleForUsers.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {visibleForUsers.map((u) => (
                    <span
                      key={u.user_id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-200 text-sm"
                    >
                      {u.name || u.email || u.user_id}
                      <button
                        type="button"
                        onClick={() => setVisibleForUsers(prev => prev.filter(x => x.user_id !== u.user_id))}
                        className="text-gray-600 hover:text-black"
                        aria-label="Remove"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold mb-1">Location Name</label>
            <input
              type="text"
              value={newClass.locationName}
              onChange={(e) => setNewClass({ ...newClass, locationName: e.target.value })}
              className="w-full border border-gray-300 rounded p-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Location URL</label>
            <input
              type="url"
              value={newClass.locationUrl}
              onChange={(e) => setNewClass({ ...newClass, locationUrl: e.target.value })}
              className="w-full border border-gray-300 rounded p-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Video call link</label>
            <input
              type="url"
              value={newClass.videoCallUrl}
              onChange={(e) => setNewClass({ ...newClass, videoCallUrl: e.target.value })}
              className="w-full border border-gray-300 rounded p-2 text-sm"
              placeholder="Select or paste..."
              required={newClass.type === 'online' || newClass.allowJoinOnline}
            />
            <p className="text-xs text-gray-500 mt-0.5">Shown to users who are booked for this class as &quot;Join online&quot;</p>
          </div>

          {newClass.type !== 'online' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newClass.allowJoinOnline}
                onChange={(e) => setNewClass({ ...newClass, allowJoinOnline: e.target.checked })}
                className="rounded border-gray-300"
              />
              Allow joining online
              <span className="text-xs text-gray-500">(any active pass may attend this class remotely)</span>
            </label>
          )}

          <div className="flex justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-brown text-white hover:bg-pink text-sm"
            >
              {isDuplicate ? 'Create Duplicate' : isEditMode ? 'Save Changes' : 'Create Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}